import * as React from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { Count, UnreadDot } from '@/components/ui/badge'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

/**
 * The Alumnia UI 1.2.1 button (docs/redesign/sistema/Button.md), reached with
 * `look="encuentro"`. It reads only `--en-*` tokens, which exist under
 * `[data-ui='encuentro']`, so it is inert in classic mode.
 *
 * - Height is a floor, never a box: `min-h-en-control` (44 px, the touch
 *   target) or `min-h-en-control-compact` (36 px, with a 44 px hit area: 5px past the padding box on each side, because the 1px border is outside it).
 *   Type, padding and icons are in rem and the label wraps, so with the text
 *   at 200 % the button grows instead of clipping (docs/redesign/README.md).
 * - Focus: a 2 px `en-focus` (electric) outline, 2 px away.
 */
const encuentroButtonVariants = cva(
  [
    'relative inline-flex max-w-full min-w-0 items-center justify-center gap-2 rounded-en-md border border-transparent',
    'font-en-sans font-semibold text-center whitespace-normal [overflow-wrap:anywhere] en-large-text:hyphens-auto cursor-pointer select-none no-underline',
    'transition-[background-color,border-color,transform] duration-(--en-dur-feedback) ease-(--en-ease) active:scale-[0.98]',
    'motion-reduce:transition-none motion-reduce:active:scale-100',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus',
    'disabled:cursor-not-allowed disabled:opacity-45 disabled:active:scale-100',
    'aria-disabled:cursor-not-allowed aria-disabled:opacity-45 aria-disabled:active:scale-100',
    // Icons are 1.25rem (the 20 px `icon` token at the default size) so they grow with the text.
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-en-primary text-en-primary-ink shadow-[0_1px_2px_rgb(23_33_57/0.16)] hover:bg-[color-mix(in_srgb,var(--en-primary)_86%,var(--en-electric))]',
        secondary: 'bg-en-surface text-en-ink border-en-line-control hover:bg-en-surface-sunken',
        ghost: 'bg-transparent text-en-ink hover:bg-en-surface-sunken',
        danger: 'bg-en-surface text-en-danger border-en-danger hover:bg-en-danger-soft',
      },
      size: {
        md: 'min-h-en-control px-5 py-2 text-[0.9375rem]/[1.33]',
        sm: "min-h-en-control-compact px-3 py-1.5 text-en-label after:absolute after:-inset-x-0.5 after:-inset-y-[5px] after:content-['']",
      },
      iconOnly: {
        true: 'shrink-0',
        false: '',
      },
      block: {
        true: 'w-full',
        false: '',
      },
      done: {
        true: 'border-transparent bg-en-positive-soft text-en-positive shadow-none hover:bg-en-positive-soft',
        false: '',
      },
    },
    compoundVariants: [
      // Square: the floor is the control size; the icon (rem) plus padding grows past it.
      { iconOnly: true, size: 'md', class: 'min-w-en-control p-2' },
      // The hit area is measured from the padding box, inside the 1px border: 5px a side
      // (not 4) makes 36 + 8 = 44 px, and a square one reaches 44 px wide too.
      { iconOnly: true, size: 'sm', class: 'min-w-en-control-compact p-1.5 after:-inset-x-[5px]' },
    ],
    defaultVariants: { size: 'md', iconOnly: false, block: false, done: false },
  }
)

type EncuentroButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

type ClassicButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    look?: 'classic'
  }

type EncuentroButtonBase = Omit<React.ComponentProps<'button'>, 'aria-label'> & {
  look: 'encuentro'
  /** Required: one `primary` per screen; card and row actions in feeds and lists are `ghost`. */
  variant: EncuentroButtonVariant
  /** `sm` is 36 px tall and keeps a 44 px hit area. */
  size?: 'md' | 'sm'
  /** Full width. */
  block?: boolean
  /** A fulfilled state, such as "Anotado" (`a-btn--done`). */
  done?: boolean
  /** Unread dot in `electric` (a novelty, not a counter). */
  dot?: boolean
  asChild?: boolean
}

type EncuentroTextButtonProps = EncuentroButtonBase & {
  iconOnly?: false
  'aria-label'?: string
  count?: never
}

type EncuentroIconButtonProps = EncuentroButtonBase & {
  /** The icon is the only child: the accessible name is required. */
  iconOnly: true
  /** Carries the name and, with `count`, the number ("Notificaciones, 4 nuevas"). */
  'aria-label': string
  /** Counter in `spark-strong` with a white number. Only for what needs action; hidden at 0. */
  count?: number
}

type EncuentroButtonProps = EncuentroTextButtonProps | EncuentroIconButtonProps

type ButtonProps = ClassicButtonProps | EncuentroButtonProps

function EncuentroButton({
  className,
  variant,
  size,
  iconOnly,
  block,
  done,
  dot,
  count,
  asChild = false,
  children,
  look: _look,
  ...props
}: EncuentroButtonProps) {
  const Comp = asChild ? Slot : 'button'
  const hasCount = typeof count === 'number' && count > 0

  return (
    <Comp
      // A plain button, not a form submit, unless the caller says so.
      type={asChild ? undefined : 'button'}
      data-slot="button"
      data-look="encuentro"
      data-variant={variant}
      className={cn(encuentroButtonVariants({ variant, size, iconOnly, block, done }), className)}
      {...props}
    >
      <Slottable>{children}</Slottable>
      {hasCount && <Count value={count} aria-hidden className="absolute top-0.5 right-0" />}
      {dot && !hasCount && <UnreadDot className="absolute top-2 right-2" />}
    </Comp>
  )
}

function Button(props: ButtonProps) {
  if (props.look === 'encuentro') return <EncuentroButton {...props} />

  const { className, variant, size, asChild = false, look: _look, ...rest } = props
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...rest}
    />
  )
}

export { Button, buttonVariants, encuentroButtonVariants }
export type { ButtonProps, EncuentroButtonProps, EncuentroButtonVariant }
